import { useMemo, useState } from 'react'
import {
  AlertTriangle,
  Bug,
  CheckCheck,
  Clock,
  Inbox,
  Lightbulb,
  Loader2,
  User as UserIcon,
} from 'lucide-react'
import { Alert, AlertDescription, AlertTitle } from '@/components/ui/alert'
import { Badge } from '@/components/ui/badge'
import { Button } from '@/components/ui/button'
import { Card, CardContent } from '@/components/ui/card'
import { Skeleton } from '@/components/ui/skeleton'
import { useAuth } from '@/features/auth/useAuth'
import { markFeedbackReviewed, FEEDBACK_PAGE_LIMIT } from '@/features/feedback/feedback-api'
import { useFeedback } from '@/features/feedback/useFeedback'
import { describeWriteError } from '@/features/users/permissions'
import type { FeedbackMessage, FeedbackType } from '@/lib/types'
import { cn, formatDateTime } from '@/lib/utils'

const TABS: ReadonlyArray<{ type: FeedbackType; label: string; icon: typeof Bug }> = [
  { type: 'problem', label: 'Проблемы', icon: Bug },
  { type: 'suggestion', label: 'Предложения', icon: Lightbulb },
]

/** Stage 3: the "Сообщи нам" messages sent from the mobile app. */
export function FeedbackPage() {
  const { user } = useAuth()
  const [type, setType] = useState<FeedbackType>('problem')
  const [onlyNew, setOnlyNew] = useState(false)
  const [pendingId, setPendingId] = useState<string | null>(null)
  const [error, setError] = useState<string | null>(null)
  const { items, isLoading, error: loadError } = useFeedback(type)

  const newCount = items.filter((item) => item.status === 'new').length
  const visible = useMemo(
    () => (onlyNew ? items.filter((item) => item.status === 'new') : items),
    [items, onlyNew],
  )

  const handleReview = async (message: FeedbackMessage) => {
    setPendingId(message.id)
    setError(null)
    try {
      await markFeedbackReviewed(message.id, user?.email ?? user?.uid ?? 'admin')
    } catch (caught) {
      setError(describeWriteError(caught))
    } finally {
      setPendingId(null)
    }
  }

  return (
    <div className="mx-auto flex max-w-3xl flex-col gap-5">
      <div>
        <h1 className="text-2xl font-semibold">Сообщения</h1>
        <p className="text-muted-foreground text-sm">
          То, что пользователи присылают из приложения через «Сообщи нам». Текст изменить нельзя —
          он принадлежит автору.
        </p>
      </div>

      <div className="flex flex-wrap items-center gap-2">
        <div className="bg-muted flex rounded-lg p-1">
          {TABS.map((tab) => (
            <button
              key={tab.type}
              type="button"
              onClick={() => setType(tab.type)}
              aria-pressed={type === tab.type}
              className={cn(
                'flex cursor-pointer items-center gap-2 rounded-md px-3 py-1.5 text-sm transition-colors',
                type === tab.type
                  ? 'bg-background text-foreground shadow-xs'
                  : 'text-muted-foreground hover:text-foreground',
              )}
            >
              <tab.icon className="size-4" />
              {tab.label}
            </button>
          ))}
        </div>

        <Button
          variant={onlyNew ? 'default' : 'outline'}
          size="sm"
          onClick={() => setOnlyNew((value) => !value)}
        >
          Только новые ({newCount})
        </Button>
      </div>

      {loadError ? (
        <Alert variant="destructive">
          <AlertTriangle />
          <AlertTitle>Список не загружен</AlertTitle>
          <AlertDescription>{loadError}</AlertDescription>
        </Alert>
      ) : null}

      {error ? (
        <Alert variant="destructive">
          <AlertTriangle />
          <AlertTitle>Статус не изменён</AlertTitle>
          <AlertDescription>{error}</AlertDescription>
        </Alert>
      ) : null}

      {isLoading ? (
        <div className="flex flex-col gap-3">
          {[0, 1, 2].map((row) => (
            <Skeleton key={row} className="h-28 w-full" />
          ))}
        </div>
      ) : visible.length === 0 ? (
        <Card>
          <CardContent className="text-muted-foreground flex flex-col items-center gap-2 py-16 text-sm">
            <Inbox className="size-8" />
            {items.length === 0 ? 'Пока ничего нет.' : 'Все сообщения уже просмотрены.'}
          </CardContent>
        </Card>
      ) : (
        <div className="flex flex-col gap-3">
          {visible.map((message) => (
            <MessageCard
              key={message.id}
              message={message}
              isPending={pendingId === message.id}
              onReview={() => void handleReview(message)}
            />
          ))}
        </div>
      )}

      <p className="text-muted-foreground text-xs">
        «Просмотрено» ставится для всех и обратно не снимается — сначала прочитайте сообщение.
        Загружается до {FEEDBACK_PAGE_LIMIT} последних сообщений каждого вида.
      </p>
    </div>
  )
}

function MessageCard({
  message,
  isPending,
  onReview,
}: {
  message: FeedbackMessage
  isPending: boolean
  onReview: () => void
}) {
  const isNew = message.status === 'new'
  return (
    <Card className={cn('gap-3 py-4', !isNew && 'opacity-75')}>
      <CardContent className="flex flex-col gap-3">
        <div className="flex flex-wrap items-center gap-x-4 gap-y-1 text-xs">
          <span className="flex items-center gap-1.5">
            <UserIcon className="text-muted-foreground size-3.5" />
            <span className="font-medium">{message.userName || 'Неизвестный пользователь'}</span>
          </span>
          <span className="text-muted-foreground flex items-center gap-1.5">
            <Clock className="size-3.5" />
            {message.createdAt === null ? 'отправляется…' : formatDateTime(message.createdAt)}
          </span>
          {isNew ? (
            <Badge>Новое</Badge>
          ) : (
            <Badge variant="secondary" className="gap-1">
              <CheckCheck className="size-3" />
              Просмотрено
            </Badge>
          )}
        </div>

        <p className="text-sm whitespace-pre-wrap">{message.text}</p>

        {isNew ? (
          <div>
            <Button variant="outline" size="sm" onClick={onReview} disabled={isPending}>
              {isPending ? <Loader2 className="animate-spin" /> : <CheckCheck />}
              Отметить просмотренным
            </Button>
          </div>
        ) : message.reviewedBy ? (
          <p className="text-muted-foreground text-xs">
            Просмотрел: {message.reviewedBy}
            {message.reviewedAt !== null ? ` · ${formatDateTime(message.reviewedAt)}` : ''}
          </p>
        ) : null}
      </CardContent>
    </Card>
  )
}
